import { copyText } from '#lib/platform/clipboard.js';
import { resolve } from '$app/paths';

import type { RoomListItemView } from '#src/generated/protocol';

import type { CoreClient } from '#lib/core/client.svelte.js';

import { splitVia } from '#lib/rooms/join-address.js';
import { parseMatrixLink } from '#lib/rooms/matrix-link.js';

import { findRoomByPathId, roomPathParam, roomPathParamFromId } from './room-list.svelte';

function parentSpaceOf(
  rooms: readonly RoomListItemView[],
  roomId: string
): RoomListItemView | undefined {
  return rooms.find(
    (space) =>
      space.room_kind === 'space' &&
      space.state === 'joined' &&
      !space.is_tombstoned &&
      space.space_children.some((child) => child.room_id === roomId)
  );
}

function rootSpaceOf(
  rooms: readonly RoomListItemView[],
  roomId: string
): RoomListItemView | undefined {
  const seen = new Set<string>([roomId]);
  let space = parentSpaceOf(rooms, roomId);

  while (space !== undefined && !seen.has(space.room_id)) {
    seen.add(space.room_id);
    const parent = parentSpaceOf(rooms, space.room_id);
    if (parent === undefined) return space;
    space = parent;
  }

  return space;
}

/** Mirrors RoomNav's sectioning, so a permalink lands where the sidebar links. */
function sectionPath(
  rooms: readonly RoomListItemView[],
  room: RoomListItemView | undefined,
  roomParam: string
): string {
  if (room?.room_kind === 'space')
    return resolve('/(app)/space/[spaceId]/lobby', { spaceId: roomParam });
  if (room?.is_direct) return resolve('/(app)/direct/[roomId]', { roomId: roomParam });

  const parentSpace = room ? rootSpaceOf(rooms, room.room_id) : undefined;
  if (parentSpace) {
    return resolve('/(app)/space/[spaceId]/[roomId]', {
      spaceId: roomPathParam(parentSpace, rooms),
      roomId: roomParam,
    });
  }

  // An unknown room still resolves: the timeline reports the failure with more
  // context than a redirect to nowhere would.
  return resolve('/(app)/rooms/[roomId]', { roomId: roomParam });
}

export function roomSectionPath(
  rooms: readonly RoomListItemView[],
  roomIdOrAlias: string,
  eventId?: string | null,
  via: readonly string[] = []
): string {
  const room = findRoomByPathId(rooms, roomIdOrAlias);
  const base = sectionPath(
    rooms,
    room,
    room ? roomPathParam(room, rooms) : roomPathParamFromId(roomIdOrAlias)
  );

  const query = new URLSearchParams();
  // A space has no timeline to focus an event in.
  if (eventId && room?.room_kind !== 'space') query.set('event', eventId);
  // `via` only helps a join, so it rides along solely for a room the client has
  // never seen. Carrying it further would pin it in the URL of a room already open.
  if (room === undefined) for (const server of via) query.append('via', server);

  const search = query.toString();
  return search === '' ? base : `${base}?${search}`;
}

/** An alias resolves through its own domain; only a room id needs routing help. */
export function viaFor(address: string, via: readonly string[]): string[] {
  return address.startsWith('#') ? [] : [...via];
}

/**
 * A matrix.to link others can follow; a room id needs `via` to be routable.
 */
export function matrixToUrl(
  address: string,
  via: readonly string[],
  eventId?: string | null
): string {
  const path = eventId
    ? `${encodeURIComponent(address)}/${encodeURIComponent(eventId)}`
    : encodeURIComponent(address);

  // The query belongs inside the fragment, so it cannot be built with `URL`.
  const query = new URLSearchParams(
    viaFor(address, via).map((server) => ['via', server])
  ).toString();
  return `https://matrix.to/#/${path}${query === '' ? '' : `?${query}`}`;
}

export async function copyRoomLink(
  core: Pick<CoreClient, 'rpc'>,
  room: Pick<RoomListItemView, 'room_id' | 'canonical_alias'>,
  eventId?: string | null
): Promise<boolean> {
  try {
    const via = room.canonical_alias
      ? []
      : await core.rpc.rooms.viaServers({ room_id: room.room_id });
    await copyText(matrixToUrl(room.canonical_alias ?? room.room_id, via, eventId));
    return true;
  } catch (error) {
    console.debug('[sable room] copy link failed', error);
    return false;
  }
}

/** A room link resolves to a route; a user link has no route of its own. */
export type PermalinkTarget = { kind: 'room'; path: string } | { kind: 'user'; userId: string };

/**
 * Resolves the tail of a `/to/...` URL, still percent-encoded so the matrix.to parser decodes it.
 */
export function permalinkTarget(
  rooms: readonly RoomListItemView[],
  encodedFragment: string
): PermalinkTarget | null {
  // `?via=` sits inside the fragment, where it would otherwise be parsed as
  // part of the last id.
  const { href, via } = splitVia(`https://matrix.to/#/${encodedFragment}`);
  const link = parseMatrixLink(href);
  if (link === null) return null;
  if (link.kind === 'user') return { kind: 'user', userId: link.user_id };

  return {
    kind: 'room',
    path: roomSectionPath(rooms, link.room_id, link.kind === 'event' ? link.event_id : null, via),
  };
}
